import React, { useEffect, useState } from "react";
import {
  Paper,
  Stack,
  Divider,
  Collapse,
  IconButton,
  Typography,
  useTheme,
  Box,
} from "@mui/material";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import {
  CartEvent,
  selectCartManager,
} from "@/global/store/data/purchaseSeats/cartManager";
import { useAppStore } from "@/global/store";
import { useCartHelpers } from "../../useCartHelpers";
import {
  findEventByCartEvent,
  sortCartEventsBySelection,
} from "../../utils.cart";
import {
  purchaseSeatsTabUIDs as tabUIDs,
  vendorUUIDs,
} from "@/global/config/constants";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { selectEventsSelected } from "@/global/store/data/purchaseSeats/events";
import { CheckoutEventCard } from "./CheckoutEventCard";
import { EditSeatsButton } from "./EditSeatsButton";
import { useEditSeatsTrack } from "@/global/hooks/useEditSeatsTrack";
import { calcDefaultPurchaseSeatTab } from "../../utils.purchaseSeats";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";

const YourTickets = (props: {
  title?: string | React.ReactNode;
  description?: string | React.ReactNode;
  isCollapseClosed?: boolean;
  hideCollapseIcon?: boolean;
}) => {
  const theme = useTheme();
  const { md: mdDown } = useCssMediaQuery("down");
  const [isCollapseOpen, setIsCollapseOpen] = useState(true);
  const teamConfig = useTeamConfig();
  const {
    platform,
    purchaseSeatConfigs: { isCheckoutEditSeatsEnabled },
  } = teamConfig;

  const selectedEvents = useAppStore(selectEventsSelected);
  const cartState = useAppStore(selectCartManager);
  const cartEvents = sortCartEventsBySelection(cartState.cart, selectedEvents);
  const [editingEventCode, setEditingEventCode] = React.useState("");
  const { removeCartProductsFromCartState, switchPurchaseSeatTab } =
    useCartHelpers();
  const { set: setEditSeatsClicked } = useEditSeatsTrack();

  const handleClickEditSeat = async (cartEvent: CartEvent) => {
    if (platform == vendorUUIDs.tickesdotcom) {
      setEditSeatsClicked({ isEditSeatsClicked: true });
    }
    const event = findEventByCartEvent(selectedEvents, cartEvent);
    setEditingEventCode(cartEvent.eventCode);

    if (platform == vendorUUIDs.tickesdotcom && !cartEvent?.offerId) {
      switchPurchaseSeatTab(calcDefaultPurchaseSeatTab(teamConfig));
      setEditingEventCode("");
      return;
    }

    await switchPurchaseSeatTab(tabUIDs.chooseSeats, event?.eventCode);
    setEditingEventCode("");
  };

  const handleCollapse = () => {
    setIsCollapseOpen(!isCollapseOpen);
  };

  React.useEffect(() => {
    if (props?.isCollapseClosed) {
      setIsCollapseOpen(false);
    }
  }, []);

  return (
    <Paper variant="outlined" sx={{ minHeight: "100%" }}>
      <Stack>
        <Stack direction="row" justifyContent={"space-between"} margin={"24px"}>
          <Box>
            <Typography fontWeight={700} fontSize={"24px"}>
              {props?.title || "Your Tickets"}
            </Typography>

            <Typography
              variant="body2"
              color={theme.palette.grey[600]}
              lineHeight={1.2}
              fontSize={"14px"}
            >
              {props?.description}
            </Typography>
          </Box>
          <Box flexGrow={1} />
          {mdDown && !props?.hideCollapseIcon && (
            <Box minWidth={"40px"}>
              <IconButton
                onClick={handleCollapse}
                size="large"
                sx={{
                  color: theme.palette.grey[800],
                  backgroundColor: `${theme.palette.grey[100]} !important`,
                  "&:active": { backgroundColor: theme.palette.grey[200] },
                }}
              >
                {isCollapseOpen ? (
                  <CloseRoundedIcon />
                ) : (
                  <ExpandMoreRoundedIcon />
                )}
              </IconButton>
            </Box>
          )}
        </Stack>
        {isCollapseOpen ? <Divider /> : null}
        <Collapse
          in={isCollapseOpen}
          sx={{ height: isCollapseOpen ? "100%" : "fit-content" }}
        >
          <Stack p={3} gap={3}>
            {cartEvents.map((cartEvent) => {
              return (
                <CheckoutEventCard
                  key={cartEvent.eventCode}
                  cartEvent={cartEvent}
                  renderHeaderActionItems={() => {
                    if (isCheckoutEditSeatsEnabled) {
                      return (
                        <EditSeatsButton
                          cartEvent={cartEvent}
                          editingEventCode={editingEventCode}
                          onClick={handleClickEditSeat}
                        />
                      );
                    }
                  }}
                />
              );
            })}
          </Stack>
        </Collapse>
      </Stack>
    </Paper>
  );
};

export default YourTickets;
